import React, { useEffect, useState, useCallback } from "react";
import { X, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Modal di eliminazione definitiva CLIENTE (pattern GitHub type-name-to-confirm).
 * - Fetcha `/internal/clients/{id}/delete-preview` per mostrare i counts
 * - Bottone abilitato solo quando l'input matcha esattamente `company_name`
 */
export default function DeleteClientModal({ open, onClose, client, onDeleted }) {
    const [confirmText, setConfirmText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setConfirmText("");
            setError(null);
            setPreview(null);
            return;
        }
        if (!client?.id) return;
        setPreviewLoading(true);
        api.get(`/internal/clients/${client.id}/delete-preview`)
            .then(({ data }) => setPreview(data))
            .catch(() => setPreview(null))
            .finally(() => setPreviewLoading(false));
    }, [open, client?.id]);

    const doDelete = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await api.delete(`/internal/clients/${client.id}`);
            onDeleted?.();
        } catch (e) {
            setError(e?.response?.data?.detail || "Eliminazione fallita");
        } finally {
            setLoading(false);
        }
    }, [client?.id, onDeleted]);

    if (!open || !client) return null;
    const targetName = client.company_name || "";
    const canConfirm = confirmText.trim() === targetName && !loading;

    return (
        <div className="lt-modal-backdrop" onClick={loading ? undefined : onClose} data-testid="delete-client-modal">
            <div className="lt-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
                <div className="lt-modal__head" style={{ borderBottom: "1px solid #F2E9E5" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 2,
                            background: "rgba(220, 38, 38, 0.1)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <AlertTriangle size={18} color="#DC2626" />
                        </div>
                        <h2 className="lt-modal__title" style={{ margin: 0 }}>Eliminazione definitiva</h2>
                    </div>
                    <button className="lt-icon-btn" onClick={onClose} disabled={loading} data-testid="delete-client-close">
                        <X size={16} />
                    </button>
                </div>

                <div style={{ padding: "20px 4px 4px" }}>
                    <p style={{ margin: "0 0 12px", color: "#181B20", fontSize: 14, lineHeight: 1.5 }}>
                        Eliminerà definitivamente{" "}
                        <strong style={{ color: "#181B20" }}>{targetName}</strong>{" "}
                        e tutti i dati collegati:
                    </p>

                    {previewLoading && (
                        <div style={{ padding: "12px 0", color: "#8A8F88", fontSize: 13 }}>Calcolo dati collegati…</div>
                    )}
                    {preview && (
                        <ul style={{
                            listStyle: "none", padding: "10px 14px", margin: "0 0 14px",
                            background: "#FAFAF7", border: "1px solid #EEEFEA", borderRadius: 4,
                            fontSize: 13, color: "#5F6560", lineHeight: 1.9,
                        }} data-testid="delete-client-preview">
                            <li><strong style={{ color: "#181B20" }}>{preview.tickets}</strong> ticket · <strong style={{ color: "#181B20" }}>{preview.threads}</strong> conversazioni · <strong style={{ color: "#181B20" }}>{preview.messages}</strong> messaggi</li>
                            <li><strong style={{ color: "#181B20" }}>{preview.documenti}</strong> documenti · <strong style={{ color: "#181B20" }}>{preview.attachments}</strong> allegati</li>
                            <li><strong style={{ color: "#181B20" }}>{preview.scadenze}</strong> scadenze · <strong style={{ color: "#181B20" }}>{preview.checklists}</strong> checklist · <strong style={{ color: "#181B20" }}>{preview.spese}</strong> spese</li>
                            <li><strong style={{ color: "#181B20" }}>{preview.client_users}</strong> utenti cliente</li>
                        </ul>
                    )}

                    <div style={{
                        padding: "10px 12px",
                        background: "rgba(220, 38, 38, 0.06)",
                        border: "1px solid rgba(220, 38, 38, 0.2)",
                        borderRadius: 4,
                        marginBottom: 16,
                    }}>
                        <div style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            color: "#DC2626",
                            marginBottom: 4,
                            fontWeight: 600,
                        }}>
                            Operazione irreversibile
                        </div>
                        <div style={{ fontSize: 13, color: "#5F6560", lineHeight: 1.5 }}>
                            I file caricati saranno rimossi definitivamente dallo storage.
                        </div>
                    </div>

                    <label className="lt-label" htmlFor="delete-client-confirm-input">
                        Per confermare, digita il nome della società
                    </label>
                    <input
                        id="delete-client-confirm-input"
                        className="lt-input"
                        placeholder={targetName}
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        disabled={loading}
                        autoFocus
                        data-testid="delete-client-confirm-input"
                    />
                    <p className="lt-toggle__hint" style={{ marginTop: 4, fontSize: 12, color: "#8A8F88" }}>
                        Deve corrispondere esattamente (case-sensitive).
                    </p>

                    {error && (
                        <div style={{
                            marginTop: 12, padding: "10px 12px", fontSize: 13,
                            background: "rgba(220, 38, 38, 0.08)",
                            border: "1px solid rgba(220, 38, 38, 0.25)",
                            borderRadius: 4, color: "#DC2626",
                        }} data-testid="delete-client-error">
                            {error}
                        </div>
                    )}

                    <div className="lt-form-actions" style={{ marginTop: 20 }}>
                        <div style={{ flex: 1 }} />
                        <button
                            type="button"
                            className="lt-btn-secondary"
                            onClick={onClose}
                            disabled={loading}
                            autoFocus={false}
                        >
                            Annulla
                        </button>
                        <button
                            type="button"
                            onClick={doDelete}
                            disabled={!canConfirm}
                            data-testid="delete-client-confirm-btn"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "10px 18px",
                                borderRadius: 2,
                                border: "1px solid #DC2626",
                                background: canConfirm ? "#DC2626" : "transparent",
                                color: canConfirm ? "#FFFFFF" : "#DC2626",
                                fontFamily: "'Archivo', system-ui, sans-serif",
                                fontWeight: 500,
                                fontSize: 14,
                                cursor: canConfirm ? "pointer" : "not-allowed",
                                opacity: canConfirm ? 1 : 0.4,
                                transition: "background-color .15s ease, color .15s ease",
                            }}
                        >
                            {loading ? "Eliminazione…" : "Elimina definitivamente"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
